import { useRecoilState, useRecoilValue } from 'recoil';
import { Button } from 'components/core';
import { builderAnswersState, visualizationsState } from 'states';
import { FunnelAnswers, FunnelStage } from 'helpers/kpiBuilder/types';
import { BuilderSection } from './BuilderSection';
import { FunnelStageRow, KpiOption } from './FunnelStageRow';
import { OptionCards } from './OptionCards';
import { OUTLINE_SMALL } from './buttonProps';

const KINDS = [
  { id: 'stage', label: 'Steps in a row', desc: 'e.g. Registered → Enrolled → Completed. Each step shows its count, the share that moved on, and who is still waiting.' },
  { id: 'conversion', label: 'One start, several outcomes', desc: 'e.g. Registered, with Enrolled, Self-assessed and Completed side by side, each as a share of the start.' },
];

const blank = (): FunnelStage => ({ kpi: '', label: '', gapKpi: '', gapLabel: '', pill: '' });
// A funnel needs at least two steps, so two empty ones are shown until the admin fills them in.
const withMinimum = (stages: FunnelStage[]): FunnelStage[] => (stages.length >= 2 ? stages : [...stages, ...Array(2 - stages.length).fill(null).map(blank)]);

// Funnel question: connect existing KPIs, one per step.
export const FunnelStep = () => {
  const [a, setA] = useRecoilState(builderAnswersState);
  const visualizations = useRecoilValue(visualizationsState);
  const stages = withMinimum(a.funnel.stages);
  const kpis = visualizations.filter((v) => v.visualizationCode && v.data?.[0]?.info);
  const info = (v: (typeof kpis)[number]) => v.data[0].info;
  const toOption = (v: (typeof kpis)[number]): KpiOption => ({ code: v.visualizationCode, title: v.title });
  // A step shows one plain number (total registered, enrolled, completed). KPIs that list who is missing
  // go in the optional “still waiting” slot instead; used as a step they return no number to show.
  const options = kpis.filter((v) => info(v).chartType === 'metric' && !info(v).handlerKey).map(toOption);
  const gapOptions = kpis.filter((v) => info(v).chartType === 'notenrolledanalysis' || info(v).handlerKey === 'notenrolledanalysis').map(toOption);
  const plain = new Set(options.map((o) => o.code));
  // A saved choice must always show, even when it is not one the pickers offer for new steps.
  const withSaved = (list: KpiOption[], code: string): KpiOption[] =>
    !code || list.some((o) => o.code === code) ? list : [...list, kpis.map(toOption).find((o) => o.code === code) ?? { code, title: code }];

  const save = (next: FunnelAnswers) => setA({ ...a, funnel: next });
  const update = (i: number, patch: Partial<FunnelStage>) => save({ ...a.funnel, stages: stages.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const add = () => {
    save({ ...a.funnel, stages: [...stages, blank()] });
  };

  return (
    <>
      <BuilderSection title="What kind of funnel?" hint="A funnel connects KPIs you already have, one per step. Each step must be a KPI that shows one plain number, such as total registered, enrolled or completed. KPIs that list who is missing, like Not Enrolled, go in the “still waiting” box of the step before.">
        <OptionCards cards={KINDS} selected={[a.funnel.kind]} onSelect={(id) => save({ ...a.funnel, kind: id as FunnelAnswers['kind'] })} />
      </BuilderSection>
      <BuilderSection title="Steps" hint={options.length ? 'The first step is the starting number. Each next step is shown as a share of the one before it.' : 'No plain single-number KPIs exist yet. Create the KPI for each step first, for example with the “How many…?” question, then come back.'}>
        {stages.map((s, i) => (
          <FunnelStageRow
            key={i}
            index={i}
            stage={s}
            withGap={a.funnel.kind === 'stage' && i < stages.length - 1}
            options={withSaved(options, s.kpi)}
            gapOptions={withSaved(gapOptions, s.gapKpi)}
            notPlain={!!s.kpi && !plain.has(s.kpi)}
            canRemove={stages.length > 2}
            onChange={(patch) => update(i, patch)}
            onRemove={() => save({ ...a.funnel, stages: stages.filter((_, j) => j !== i) })}
          />
        ))}
        <Button {...OUTLINE_SMALL} onClick={add}>
          Add a step
        </Button>
      </BuilderSection>
    </>
  );
};
